import React, { useState, useEffect, useContext, useMemo } from "react";
import Routes from "./routes";
import "react-toastify/dist/ReactToastify.css";
import "./styles/theme.css";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import {
  createTheme as createThemeV5,
  ThemeProvider as ThemeProviderV5,
} from "@mui/material/styles";
import { ptBR } from "@material-ui/core/locale";
import CssBaseline from "@material-ui/core/CssBaseline";
import whatsBackground from "../src/assets/wa-background.png";
import whatsBackground2 from "../src/assets/wa-background-black.png";
import {
  ColorModeContext,
  ColorModeProvider,
  FONT_SIZE_DEFAULT,
} from "./context/Theme/ColorModeContext";

// Opções base do tema CLARO (comportamento atual preservado).
const lightThemeOptions = {
  typography: {
    fontSize: 10,
  },
  scrollbarStyles: {
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
      backgroundColor: "#e8e8e8",
    },
  },
  palette: {
    type: "light",
    primary: { main: "#2576d2" },
  },
  backgroundImage: `url(${whatsBackground})`,
};

// Opções do tema ESCURO — valores alinhados com os tokens em styles/theme.css.
const darkThemeOptions = {
  typography: {
    fontSize: 10,
  },
  scrollbarStyles: {
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.6)",
      backgroundColor: "#2a3942",
    },
  },
  palette: {
    type: "dark",
    primary: { main: "#3d9bf0" },
    secondary: { main: "#00a884" },
    background: {
      default: "#0b141a",
      paper: "#111b21",
    },
    text: {
      primary: "#e9edef",
      secondary: "#aebac1",
    },
    divider: "#222e35",
  },
  backgroundImage: `url(${whatsBackground2})`,
};

// Opções do tema MUI v5 (@mui/material) — espelham o tema v4 acima.
// Necessário porque o app mistura componentes v4 (@material-ui/core) e v5
// (@mui/material: Accordion, Menu, Button, etc.). O ThemeProvider v4 NÃO
// alcança os componentes v5, que sem isto renderizam no tema claro padrão
// (fundo branco) mesmo no dark mode — era o "branco" do TicketsManager.
const v5PaletteLight = {
  mode: "light",
  primary: { main: "#2576d2" },
};
const v5PaletteDark = {
  mode: "dark",
  primary: { main: "#3d9bf0" },
  secondary: { main: "#00a884" },
  background: { default: "#0b141a", paper: "#111b21" },
  text: { primary: "#e9edef", secondary: "#aebac1" },
  divider: "#222e35",
};

// Monta o ThemeProvider lendo o modo atual do ColorModeContext.
const ThemedApp = ({ locale }) => {
  const { darkMode, fontSize } = useContext(ColorModeContext);

  const theme = useMemo(() => {
    const baseOptions = darkMode ? darkThemeOptions : lightThemeOptions;
    // Escala a tipografia base do MUI (tela toda) proporcionalmente ao
    // tamanho de fonte escolhido pelo usuário. No padrão (FONT_SIZE_DEFAULT)
    // o resultado é o valor original — zero mudança no comportamento atual.
    const scaledFontSize =
      (baseOptions.typography.fontSize * fontSize) / FONT_SIZE_DEFAULT;
    return createTheme(
      {
        ...baseOptions,
        typography: { ...baseOptions.typography, fontSize: scaledFontSize },
      },
      locale
    );
  }, [darkMode, fontSize, locale]);

  const themeV5 = useMemo(
    () =>
      createThemeV5({
        palette: darkMode ? v5PaletteDark : v5PaletteLight,
        // Escala os componentes MUI v5 (tabelas de produtos, menus, etc.) junto
        // com a fonte da tela. O default do v5 já é 14 = FONT_SIZE_DEFAULT, logo
        // no tamanho padrão o valor é idêntico ao atual.
        typography: { fontSize },
      }),
    [darkMode, fontSize]
  );

  return (
    <ThemeProvider theme={theme}>
      <ThemeProviderV5 theme={themeV5}>
        <CssBaseline />
        <Routes />
      </ThemeProviderV5>
    </ThemeProvider>
  );
};

const App = () => {
  const [locale, setLocale] = useState();

  useEffect(() => {
    setLocale(ptBR);
  }, []);

  return (
    <ColorModeProvider>
      <ThemedApp locale={locale} />
    </ColorModeProvider>
  );
};

export default App;
